import { Component, OnDestroy, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { CapabilityType } from 'src/app/models/service/capability';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
// import { AddBuildVersionFormComponent } from '../add-build-version-form/add-build-version-form.component';
import { SolutionVersionDetailsComponent } from '../solution-version-details/solution-version-details.component';
import { Permission } from '../../../models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import cloneDeep from 'lodash/cloneDeep';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { RouterSegments } from 'src/app/models/router-segments';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { SolutionStatus } from 'src/app/models/service/solution-status';

const REQUIRED_PERMISSIONS = [
  Permission.ADD_SOLUTION_VERSION,
  Permission.EDIT_SOLUTION_VERSION,
  Permission.DELETE_SOLUTION_VERSION
];

@Component({
    selector: 'app-solution-versions',
    templateUrl: './solution-versions.component.html',
    styleUrls: ['./solution-versions.component.scss'],
    standalone: false
})
export class SolutionVersionsComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);

  @ViewChild(MatSort, {static: true}) sort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  CapabilityType = CapabilityType;
  RouterSegments = RouterSegments;
  solutionId: string;
  tenant: string;
  solutionVersions: any;
  dataSource: any;
  displayedColumns = ['version', 'releaseVersion', 'description', 'created', 'status'];
  paginationSize = 10;
  pageIndex = 0;
  showPagination = true;
  sortInfo: any;
  showSpinner: any;
  breadcrumbItems: BreadcrumbItem[] = [];
  isReader: boolean;
  solutionName: string;
  Permission = Permission;
  permissionsMap: { [key: string]: boolean };
  isFailed = false;
  breadcrumbItem: any;
  urlSolution: any;
  solutionItem: any;
  tenantId: string;
  SolutionStatus = SolutionStatus;

  constructor(private activatedRoute: ActivatedRoute, private toastrService: CustomToastrService,
    private solutionsService: SolutionsService, private authService: AuthService, private sidenavService: SidenavService,
    public dateTimeFormatService: DateTimeFormatService, private permissionsService: PermissionsService,
    private toastrErrorService: ToastrErrorService, private dialog: MatDialog, private router: Router) {
      
     }

  ngOnInit(): void {
    this.tenantId = this.activatedRoute.snapshot.params.id;
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.sidenavService.tenantSelectedMenu = 'TENANT_LAYOUT.MENU.SOLUTIONS';
    this.tenant = this.authService.principal.provider;
    this.solutionId = this.activatedRoute.snapshot.params.solutionId;
    this.breadcrumbItem = localStorage.getItem('tenantViewBreadcrumbItem');
    this.urlSolution = localStorage.getItem('tenantViewUrlSolution');
    this.solutionItem = localStorage.getItem('tenantViewSolutionItem');
    this.getSolutionName();
    this.getSolutionVersions();
  }

  getSolutionName(): void{
    this.solutionsService.getSolutionDetailsById(this.solutionId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      if (data) {
        this.solutionName = data.name;
        this.breadcrumbItems.push(
          {icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.tenantId] },
          {label: translate('TENANT_LAYOUT.MENU.SOLUTIONS'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SOLUTION]},
          {label: this.solutionName});
      }
    });
  }

  getSolutionVersions(): void {
    this.showSpinner = true;
    this.isFailed = false;
    this.solutionVersions = [];
    this.loadData(this.solutionVersions);
    this.solutionsService.getSolutionVersions(this.solutionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.showSpinner = false;
          this.solutionVersions = data;
          this.loadData(this.solutionVersions);
        }
      },
      (error) => {
        this.showSpinner = false;
        this.isFailed = true;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  loadData(data?): void {
    this.pageIndex = 0;
    if (this.sortInfo) {
      const sortedData = this.sortInstanceData(this.sortInfo);
      this.dataSource = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
    } else {
      this.dataSource = new MatTableDataSource(data?.slice(this.pageIndex, this.paginationSize));
    }
  }

  sortData(event): void {
    this.sortInfo = event;
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    this.solutionVersions = this.sortInstanceData(event);
    this.dataSource = new MatTableDataSource(this.solutionVersions?.slice(this.pageIndex, this.paginationSize));
  }

  sortInstanceData(event): any {
    return this.solutionVersions?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'created':
          comparatorResult = a?.dateCreated && b?.dateCreated ? new Date(a?.dateCreated)?.getTime() - new Date(b?.dateCreated)?.getTime()
            : !a?.dateCreated ? -1 : 1;
          break;
        case 'version':
          comparatorResult = a?.version && b?.version ? a?.version?.localeCompare(b?.version) : !a?.version ? -1 : 1;
          break;
        case 'releaseVersion':
          comparatorResult = a?.releaseVersion && b?.releaseVersion ? a?.releaseVersion?.localeCompare(b?.releaseVersion) : !a?.releaseVersion ? -1 : 1;
          break;
        case 'description':
          comparatorResult = a?.description && b?.description ? a?.description?.localeCompare(b?.description)
            : !a?.description ? -1 : 1;
          break;
        case 'status':
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status)
            : !a?.status ? -1 : 1;
          break;
        default:
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

  pageChanged(event): any {
    if (event.length) {
      this.pageIndex = event.pageIndex;
      this.paginationSize = event.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      const data = this.solutionVersions.slice(firstIndex, firstIndex + this.paginationSize);
      this.dataSource = new MatTableDataSource(data);
    }
  }

  versionNameClick(event): void {
    this.router.navigate(['/', this.tenant, RouterSegments.TENANT, this.tenantId, RouterSegments.SOLUTION, this.solutionId, RouterSegments.VERSIONS, event.id]);
  }

}
